import { failure, guard, success, type DataResult } from '@core/domain/result';
import { CASES_PREFIX, casePath, type BlobBackend } from './backend';

/** Файл в хранилище приложения. */
export interface StoredFile {
  fileName: string;
  sizeBytes: number;
}

/**
 * Приватное файловое хранилище — порт `DocumentStorage` Android-версии.
 *
 * Правило то же и по той же причине ([D-02](docs/DECISIONS.md)): приложение делает
 * **собственную копию** файла, и именно она становится документом дела. Юрист может
 * удалить фотографию из «Фото» или переслать файл дальше — материалы дела от этого
 * не рвутся.
 *
 * Имя файла в базе, полный путь собирается из `caseId`: перенос хранилища не требует
 * миграции данных.
 */
export function fileStore(backend: BlobBackend) {
  const extensionOf = (fileName: string) => {
    const dot = fileName.lastIndexOf('.');
    return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  };

  const sum = (files: { sizeBytes: number }[]) =>
    files.reduce((total, file) => total + file.sizeBytes, 0);

  return {
    kind: backend.kind,

    /**
     * Кладёт содержимое в хранилище дела.
     *
     * Имя файла на диске — идентификатор документа: имя, которое дал файлу человек,
     * может быть каким угодно, вплоть до совпадающего с чужим. Расширение сохраняется:
     * по нему просмотрщик понимает, чем открывать.
     */
    async store(
      caseId: string,
      documentId: string,
      originalFileName: string,
      content: Blob,
    ): Promise<DataResult<StoredFile>> {
      return guard(async () => {
        const extension = extensionOf(originalFileName);
        const fileName = extension ? `${documentId}.${extension}` : documentId;

        await backend.write(casePath(caseId, fileName), content);
        return { fileName, sizeBytes: content.size };
      });
    },

    async open(caseId: string, fileName: string): Promise<DataResult<Blob>> {
      const blob = await backend.read(casePath(caseId, fileName));
      if (!blob) return failure({ kind: 'MissingFile', fileName });

      return success(blob);
    },

    async remove(caseId: string, fileName: string): Promise<DataResult<void>> {
      return guard(() => backend.remove(casePath(caseId, fileName)));
    },

    /** Удаляет всю папку дела. Вызывается уборкой, а не удалением дела: см. D-12. */
    async removeCase(caseId: string): Promise<DataResult<void>> {
      return guard(async () => {
        const files = await backend.list(`${CASES_PREFIX}${caseId}/`);
        for (const file of files) await backend.remove(file.path);
      });
    },

    async caseSizeBytes(caseId: string): Promise<number> {
      return sum(await backend.list(`${CASES_PREFIX}${caseId}/`));
    },

    /** Сколько всего занимают документы — экран «Хранилище». */
    async totalSizeBytes(): Promise<number> {
      return sum(await backend.list(CASES_PREFIX));
    },

    /** Сколько занимают перечисленные файлы: сколько места освободит уборка. */
    async sizeOf(paths: string[]): Promise<number> {
      const all = await backend.list(CASES_PREFIX);
      const wanted = new Set(paths);

      return sum(all.filter((file) => wanted.has(file.path)));
    },

    /** Удаляет перечисленные файлы и возвращает освобождённое место. */
    async removePaths(paths: string[]): Promise<number> {
      const freed = await this.sizeOf(paths);
      for (const path of paths) await backend.remove(path);

      return freed;
    },

    /**
     * Файлы, которым не соответствует ни одна живая запись.
     *
     * Появляются двумя способами: прерванный импорт (файл записан, запись — нет)
     * и мягко удалённые документы, у которых вышло время «Отменить». Отдаём пути,
     * а решение об удалении принимает экран «Хранилище» — там же, где юрист видит,
     * сколько места освободится.
     */
    async orphans(liveFilesByCase: Map<string, Set<string>>): Promise<string[]> {
      const all = await backend.list(CASES_PREFIX);

      return all
        .map((file) => file.path)
        .filter((path) => {
          const [, caseId, fileName] = path.split('/');
          if (!caseId || !fileName) return true;

          return !liveFilesByCase.get(caseId)?.has(fileName);
        });
    },

    /**
     * Все файлы дел с их размерами — для архива ([D-47](docs/DECISIONS.md)).
     *
     * Пути отдаются целиком, потому что в архиве они и лежат целиком: `cases/<дело>/…`
     * переносится с телефона на телефон как есть, и разбирать его на части незачем.
     */
    async paths(): Promise<{ path: string; sizeBytes: number }[]> {
      return backend.list(CASES_PREFIX);
    },

    /** Читает файл по полному пути. Путь известен только архиву и уборке. */
    async readPath(path: string): Promise<Blob | null> {
      return backend.read(path);
    },

    /**
     * Пишет файл по полному пути — так архив возвращает документы на устройство.
     *
     * Обычная запись идёт через `store`, который сам собирает имя из идентификатора
     * документа. Здесь имя уже есть: его выдал тот телефон, с которого снят архив,
     * и менять его нельзя — иначе запись в базе перестанет находить свой файл.
     */
    async writePath(path: string, content: Blob): Promise<DataResult<void>> {
      return guard(() => backend.write(path, content));
    },

    /**
     * Просит браузер не вытеснять наши данные.
     *
     * Без этого хранилище считается «лучшим усилием»: система вправе очистить его,
     * когда на устройстве кончается место. Ответ ни к чему не обязывает — Safari
     * решает сам, — но не спросить было бы странно, когда внутри материалы дел.
     */
    async requestPersistence(): Promise<boolean> {
      try {
        return (await navigator.storage?.persist?.()) ?? false;
      } catch {
        return false;
      }
    },

    /** Сколько места занято и сколько браузер обещает — для экрана «Хранилище». */
    async quota(): Promise<{ usedBytes: number; quotaBytes: number } | null> {
      try {
        const estimate = await navigator.storage?.estimate?.();
        if (!estimate) return null;

        return { usedBytes: estimate.usage ?? 0, quotaBytes: estimate.quota ?? 0 };
      } catch {
        return null;
      }
    },
  };
}

export type FileStore = ReturnType<typeof fileStore>;
